import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../../../../util/api";
import {
  type CompanyCryptoFeeOverrideApi,
  type PatchCompanyCryptoFeeOverrideBody,
} from "../../../billing/components/crypto-wallet-fees/cryptoWalletFeeSettings.types";

const companyCryptoWalletOverrideQueryKey = (
  companyId: string,
  walletProvider: string
) =>
  [
    "crypto-wallet-fees",
    "company-override",
    companyId,
    walletProvider,
  ] as const;

export const useCompanyCryptoWalletFeeSettings = (
  companyId?: string,
  walletProvider?: string
) => {
  const queryClient = useQueryClient();
  const canQuery = !!companyId && !!walletProvider;

  const overrideQuery = useQuery<CompanyCryptoFeeOverrideApi | null>({
    queryKey: canQuery
      ? companyCryptoWalletOverrideQueryKey(companyId, walletProvider)
      : ["crypto-wallet-fees", "company-override", "missing"],
    queryFn: async () => {
      if (!companyId || !walletProvider) return null;
      return await api.getCompanyCryptoFeeOverride(companyId, walletProvider);
    },
    enabled: canQuery,
  });

  const patchOverride = useMutation({
    mutationFn: async (body: PatchCompanyCryptoFeeOverrideBody) => {
      if (!companyId || !walletProvider) {
        throw new Error("Company id and wallet provider are required");
      }
      return await api.patchCompanyCryptoFeeOverride(
        companyId,
        walletProvider,
        body
      );
    },
    onSuccess: (saved) => {
      if (!companyId || !walletProvider) return;
      queryClient.setQueryData(
        companyCryptoWalletOverrideQueryKey(companyId, walletProvider),
        saved
      );
    },
  });

  return {
    override: overrideQuery.data ?? null,
    loading: overrideQuery.isLoading,
    fetching: overrideQuery.isFetching,
    error: overrideQuery.error,
    reload: overrideQuery.refetch,
    patchOverride: patchOverride.mutateAsync,
    saving: patchOverride.isPending,
  };
};
